import React, { useEffect } from "react";
import { Button, LoadingSpinner, Tooltip } from "@/components";
import { useCompanyStore } from "@/store/companyStore";
import { StoreList } from "./StoreList";
import { UserCompanyList } from "./UserCompanyList";
import { formatCodeNumber } from "@/utils/formatCodeNumber";
import {
  BusinessTypeEnum,
  ECOMPANY_BUSINESS,
  RoleEnum,
  RoleManagerExcute,
} from "@/types";
import { usePerfectureStore } from "@/store/perfectureStore";
import { EditCompany } from "../company/EditCompany";
import { useAuth } from "@/contexts/AuthContext";

export const KEYS_SEARCH_PARAMS = {
  SEARCH_STORE: "s_store",
  SEARCH_USER: "s_user",
  PAGE_STORE: "p_store",
  PAGE_USER: "p_user",
  NAME_STORE: "name_store",
  ADDRESS_STORE: "address_store",
  GROUP_STORE: "group_store",
  USER_NAME: "user_name",
  USER_EMAIL: "user_email",
  USER_ROLE: "user_role",
};

const BLANK_VALUE = "";

const RESTRICTED_DETAIL_ROLES = [
  RoleEnum.COMPANY_ADMIN,
  RoleEnum.HEAD_OFFICE_USER,
];

const CompanyDetail: React.FC = () => {
  const { role, user } = useAuth();
  const id = user?.company_id;

  const { company, loadingCompany, setCompany, setUsers } = useCompanyStore();
  const { fetchPrefectures } = usePerfectureStore();

  const isRestrictedDetailRole = RESTRICTED_DETAIL_ROLES.includes(
    role || RoleEnum.OTHER
  );

  useEffect(() => {
    fetchPrefectures();
    return () => {
      setCompany(null);
      setUsers(null);
    };
  }, []);

  const companyInfoFields = company
    ? [
        {
          label: "事業所コード",
          value: company?.business_code || BLANK_VALUE,
        },
        {
          label: "法人コード",
          value: company?.corporate_code || BLANK_VALUE,
        },
        { label: "法人名", value: company.name || BLANK_VALUE },
        { label: "系列", value: company.series || BLANK_VALUE },
        { label: "商態", value: company.business || BLANK_VALUE },
        {
          label: "事業所/非事業所",
          value:
            company.business_type == BusinessTypeEnum.MEMBER
              ? "会員"
              : "非会員",
        },
        {
          label: "代表者名",
          value: company.representative_position || BLANK_VALUE,
        },
        {
          label: "電話番号",
          value: formatCodeNumber(company?.phone) || BLANK_VALUE,
        },
        {
          label: "メールアドレス",
          value: company.email || BLANK_VALUE,
        },
        { label: "郵便番号", value: company.post_code || BLANK_VALUE },
        {
          label: "住所",
          value: company.address || BLANK_VALUE,
          isAddress: true,
        },
        {
          label: "入会日",
          value: company.joining_date
            ? new Date(company.joining_date).toLocaleDateString("ja-JP")
            : BLANK_VALUE,
        },
        {
          label: "退会日",
          value: company.unsubscribe_date
            ? new Date(company.unsubscribe_date).toLocaleDateString("ja-JP")
            : BLANK_VALUE,
        },
        {
          label: "店舗表示名称",
          value: company.store_display_name || BLANK_VALUE,
        },
        {
          label: "申告履歴",
          value: company.report_history || BLANK_VALUE,
        },
        {
          label: "随時点検履歴",
          value: company.store_inspection_history || BLANK_VALUE,
        },
      ].filter((item) =>
        isRestrictedDetailRole ? item.label === "店舗表示名称" : true
      )
    : [];

  return (
    <div className="pb-4 h-full flex flex-col">
      {/* Header */}
      <h1 className="text-base font-bold leading-7 py-4 max-w-96 truncate">
        {company?.name || <span className="invisible">-</span>}
      </h1>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-4 flex-1">
        {/* Left Column */}
        <aside className="col-span-1 bg-white rounded-lg shadow flex flex-col">
          <h2 className="text-lg font-bold p-4 border-b border-gray-200">
            事業所情報
          </h2>

          {loadingCompany && company == null ? (
            <div className="flex-1 flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : company ? (
            <div className="flex-1 px-2">
              {companyInfoFields.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between text-sm p-2 border-b border-gray-200 leading-5.5 gap-x-2"
                >
                  <span className="font-semibold min-w-[22%]">
                    {item.label}
                  </span>
                  <Tooltip content={item.value}>
                    <span
                      className={`text-auro_metal_saurus text-right truncate`}
                    >
                      {item.value}
                    </span>
                  </Tooltip>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="py-4 px-4">
            {!RoleManagerExcute.COMPANY.UPDATE?.[role || RoleEnum.OTHER] ||
            (role === RoleEnum.COMPANY_ADMIN &&
              [
                ECOMPANY_BUSINESS.TS_RETAIL,
                ECOMPANY_BUSINESS.CS_RETAIL,
              ].includes(company?.business as ECOMPANY_BUSINESS)) ? null : (
              <EditCompany
                id={+(id || 0)}
                className="flex-1"
                defaultCompany={company}
                restrictFields={isRestrictedDetailRole}
              >
                <Button variant="outlined" color="primary" className="w-full">
                  情報変更
                </Button>
              </EditCompany>
            )}
          </div>
        </aside>

        {/* Right Columns */}
        <div className="flex flex-col space-y-4 col-span-2">
          {/* Sales Offices Section */}
          <StoreList />
          {/* Users Section */}
          <UserCompanyList />
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
